import classNames from 'classnames'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { SelectionModal } from 'components/layout/SelectionModal'
import { PlanTag } from 'components/planTag'
import TooltipIcon from 'components/TooltipIcon'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { PromptGroup, ToneType } from 'service/list'
import { Me } from 'service/user'
import { useScriptStore } from 'store/useScriptStore'
import { FormValues } from 'utils/type'
import useTranslation from '../../../../hooks/useTranslation'

interface ScriptSelectorProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  translatePlanName: (planName: string) => string
  promptGroupList: PromptGroup[] | undefined
  userInfo: Me
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
  promptStatus: boolean[]
  toneTooltip: boolean[]
}

export const ScriptSelector = ({
  watch,
  setValue,
  control,
  translatePlanName,
  promptGroupList,
  userInfo,
  setPromptStatus,
  setToneTooltip,
  promptStatus,
  toneTooltip,
}: PropsWithChildren<ScriptSelectorProps>) => {
  const { t } = useTranslation()
  const { promptName, setPromptName, setToneName, isModalOpen, toggleModal } =
    useScriptStore()

  const promptRefs = useRef<(HTMLOrSVGElement | null)[]>([])
  const tonRefs = useRef<(HTMLOrSVGElement | null)[]>([])
  const [toneGroup, setToneGroup] = useState<ToneType[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<
    PromptGroup | { planName: string; introduction: string }
  >({
    planName: '',
    introduction: '',
  })
  const [isMobilePromptSelectBoxOpen, setMobilePromptSelectBoxOpen] =
    useState<boolean>(false)

  const handlePromptMouseOver = (index: number) => {
    const updatedPromptState = promptStatus?.map((value, idx) =>
      idx === index ? true : value
    )

    setPromptStatus(updatedPromptState ?? [])
  }

  const handlePromptMouseLeave = (index: number) => {
    const updatedPromptState = promptStatus?.map((value, idx) =>
      idx === index ? false : value
    )
    setPromptStatus(updatedPromptState ?? [])
  }

  const handleMouseOver = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? true : value
    )

    setToneTooltip(updatedToneTooltipState)
  }

  const handleMouseLeave = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? false : value
    )
    setToneTooltip(updatedToneTooltipState)
  }
  const handleSelectPrompt = (prompt: PromptGroup) => {
    // 기존의 프롬프트와 그리고 TONE LIST가 있다
    //1. promptId 변경
    if (prompt && prompt?.usable) {
      setValue('aiPromptTypeId', prompt.id)
      setPromptName(prompt.name)
      setToneGroup(prompt?.toneGroup)
      setToneName(prompt?.toneGroup[0].name)
      setValue('aiToneTypeId', prompt?.toneGroup[0].id)
      setToneTooltip(new Array(prompt?.toneGroup?.length).fill(false))
    }
  }

  const handleMobilePromptSelect = (prompt: PromptGroup) => {
    //1.promptName, planName이 필요합니다.

    if (prompt && prompt?.usable) {
      setSelectedPrompt(prompt)
      setValue('aiPromptTypeId', prompt.id)
      setPromptName(prompt.name)
      setToneGroup(prompt?.toneGroup)
      setToneName(prompt?.toneGroup[0].name)
      setValue('aiToneTypeId', prompt?.toneGroup[0].id)
      setToneTooltip(new Array(prompt?.toneGroup?.length).fill(false))
    }
  }

  useEffect(() => {
    const aiPromptTypeId = watch('aiPromptTypeId')
    const aiToneTypeId = watch('aiToneTypeId')

    if (aiPromptTypeId && promptGroupList) {
      const [initPromptGroup] = promptGroupList.filter(
        (el) => el.id === aiPromptTypeId
      )

      setPromptName(initPromptGroup.name)
      setSelectedPrompt(initPromptGroup)

      const [intiTone] = initPromptGroup.toneGroup.filter(
        (el) => el.id === aiToneTypeId
      )

      setToneGroup(initPromptGroup.toneGroup)
      setToneName(intiTone?.name)
    }
  }, [promptGroupList, watch('aiPromptTypeId'), watch('aiToneTypeId')])

  return (
    <SelectionModal isModal={isModalOpen}>
      <div
        className={classNames('', {
          'bg-white w-[816px] h-auto  px-8 pt-8  flex flex-col  gap-8 pb-44  ':
            !isMobile,
          'bg-white  w-full    overflow-auto    flex flex-col   gap-8 pt-10 px-4':
            isMobile,
        })}
      >
        <div className="flex flex-col  gap-4  ">
          <p className="flex flex-col gap-4 md:items-start items-center">
            <span className="font-bold text-[24px] text-left">
              {t('productVideoEditor.scriptInputModeToggle.title')}
            </span>
            <span>{t('productVideoEditor.productScript.selectConceptAndTone')}</span>
          </p>
        </div>
        <div className="flex flex-col   pb-40    gap-8 md:pb-0">
          <div className="ai-prompt flex flex-col items-start">
            <p className="font-bold text-[18px] mb-[16px]">{t('productVideoEditor.productScript.concept')}</p>
            <div className="md:bg-[#EEF1F7] w-full md:h-[40px] rounded-[10px] grid grid-cols-3 md:py-1 md:px-2 ">
              {!isMobile ? (
                promptGroupList?.map((prompt, index) => {
                  return (
                    <>
                      <Controller
                        render={({ field }) => (
                          <>
                            <div
                              key={`prompt_${index}`}
                              className={classNames(
                                'flex gap-1 justify-center cursor-pointer',
                                {
                                  'rounded-[4px] bg-white':
                                    watch('aiPromptTypeId') === prompt.id,
                                }
                              )}
                              onClick={() => {
                                handleSelectPrompt(prompt)
                              }}
                            >
                              <button
                                className={classNames('', {})}
                                type="button"
                                disabled={!prompt!.usable}
                              >
                                {prompt.name}
                              </button>
                              <div className="flex items-start h-full  pt-1 ml-[2px] mr-[8px]">
                                <PlanTag
                                  planName={translatePlanName(prompt.planName)}
                                  className=""
                                />
                              </div>
                              <TooltipIcon
                                tone={prompt}
                                index={index}
                                tonRefs={promptRefs}
                                toneTooltip={promptStatus}
                                handleMouseOver={handlePromptMouseOver}
                                handleMouseLeave={handlePromptMouseLeave}
                              />
                            </div>
                          </>
                        )}
                        rules={{
                          validate: (value) => {
                            return Number(watch('aiPromptTypeId')) === 0
                              ? 'aiPromptType ID must not be 0'
                              : true
                          },
                        }}
                        name="aiPromptTypeId"
                        control={control}
                      />
                    </>
                  )
                })
              ) : (
                <>
                  <div
                    className="w-[343px] h-[52px] flex items-center border-[1px] border-[#D4D9DF] px-4 rounded-[6px] relative"
                    onClick={() => {
                      setMobilePromptSelectBoxOpen((prev) => !prev)
                    }}
                    onMouseLeave={() => {
                      setMobilePromptSelectBoxOpen((prev) => !prev)
                    }}
                  >
                    <div className="flex gap-1 ">
                      <span className="text-[18px]">{promptName}</span>
                      <div className="flex items-start h-full  pt-1 ml-[2px] mr-[8px]">
                        <PlanTag
                          planName={translatePlanName(selectedPrompt?.planName)}
                          className=""
                        />
                      </div>
                    </div>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'w-[16px] h-[16px] absolute right-2 bottom-2':
                          !isMobile,
                        'w-[16px] h-[16px] absolute right-2 bottom-4': isMobile,
                      })}
                      alt=""
                    />
                    {isMobilePromptSelectBoxOpen && (
                      <div className="border-[1px] border-[#D4D9DF w-full top-14 left-0 absolute bg-white z-50 py-3 rounded-[6px]">
                        {promptGroupList?.map((prompt, index) => {
                          return (
                            <>
                              <button
                                className="flex gap-1 h-[46px] text-[18px] px-4 items-center py-2 w-full "
                                type="button"
                                onClick={() => {
                                  handleMobilePromptSelect(prompt)
                                }}
                              >
                                <p className="">{prompt.name}</p>
                                <div className="flex items-start h-full  pt-1 ml-[2px] mr-[8px]">
                                  <PlanTag
                                    planName={translatePlanName(
                                      prompt.planName
                                    )}
                                    className=""
                                  />
                                </div>
                              </button>
                            </>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="ai-prompt flex flex-col items-start md:min-h-auto">
            <p className="font-bold text-[18px] mb-[16px]">
              {promptName} {t('productVideoEditor.productScript.tone')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[53.67px] gap-y-[16px] ">
              {toneGroup?.map((tone, index) => {
                return (
                  <>
                    <div
                      className="w-full flex justify-between md:items-center"
                      key={`tone_${tone.id}_${index}`}
                    >
                      <div className="flex items-center h-full">
                        <Controller
                          render={({ field }) => (
                            <CircularCheckbox
                              {...field}
                              id={index}
                              checked={watch('aiToneTypeId') === tone.id}
                              disabled={
                                !userInfo ||
                                userInfo?.userType === 'normalUser' ||
                                userInfo?.userType === 'lightUser'
                              }
                              onChange={() => {
                                setValue('aiToneTypeId', tone.id)
                                setToneName(tone.name)
                              }}
                              label={tone.name}
                            />
                          )}
                          name="aiToneTypeId"
                          control={control}
                        />
                        <div className="flex items-start h-full  pt-1 ml-[2px] mr-[8px]">
                          <PlanTag
                            planName={translatePlanName(tone.planName)}
                            className=""
                          />
                        </div>
                        <TooltipIcon
                          tone={tone}
                          index={index}
                          tonRefs={tonRefs}
                          toneTooltip={toneTooltip}
                          handleMouseOver={handleMouseOver}
                          handleMouseLeave={handleMouseLeave}
                        />
                      </div>
                    </div>
                  </>
                )
              })}
            </div>
          </div>
        </div>
        {isMobile && (
          <div
            className={classNames(
              'bg-white  w-[335px]   flex flex-col   gap-8 z-50 ',
              {}
            )}
          >
            <button
              className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50"
              onClick={toggleModal}
              type="button"
            >
              {t('productVideoEditor.buttons.save')}
            </button>
          </div>
        )}
      </div>
      {!isMobile && (
        <div
          className="sticky bottom-0 p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
                before:content-[''] before:absolute before:top-[-10px] before:left-0 
                before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent z-50"
        >
          <button
            className="bg-main rounded-full w-full text-white font-bold h-[60px]  z-50"
            onClick={toggleModal}
            type="button"
          >
            {t('productVideoEditor.buttons.save')}
          </button>
        </div>
      )}
    </SelectionModal>
  )
}
